import React, { useState, useEffect, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import './WhatsAppButton.css';

/* ── Config ────────────────────────────────────────────────────────────────── */

const WHATSAPP_NUMBER = process.env.REACT_APP_WHATSAPP_NUMBER || '';
const WHATSAPP_MESSAGE =
  process.env.REACT_APP_WHATSAPP_MESSAGE ||
  'Hi EMP Workspace Team, I need help regarding your HRMS platform.';

/** Delay (ms) before the button fades in after page load */
const APPEAR_DELAY_MS = 2500;

/** Delay (ms) before the tooltip auto-hides */
const TOOLTIP_HIDE_DELAY_MS = 4000;

/* ── Public route detection ─────────────────────────────────────────────────
 *
 * The button is ONLY rendered on public / marketing pages.
 * All authenticated dashboard routes (catch-all "*" → AdminLayout) are
 * implicitly excluded because they don't match any entry in the sets below.
 *
 * ─────────────────────────────────────────────────────────────────────────── */

const PUBLIC_EXACT_ROUTES = new Set([
  '/sign-in',
  '/index',
  '/get-started',
  '/forgot-password',
  '/reset-password',
  '/download',
  // '/index' already covers new homepage
  '/404',
  '/hr-software',
  '/employee-portal',
  '/attendance-management',
  '/payroll-management',
  '/hr-analytics',
  '/employee-self-service',
  '/performance-management',
  '/blog/best-hr-software',
  '/blog/employee-portal-benefits',
  '/blog/attendance-management-guide',
]);

/** Pathname prefixes that are always public */
const PUBLIC_PREFIXES = ['/index/'];

function isPublicRoute(pathname) {
  if (PUBLIC_EXACT_ROUTES.has(pathname)) return true;
  return PUBLIC_PREFIXES.some((prefix) => pathname.startsWith(prefix));
}

/* ── WhatsApp SVG icon ─────────────────────────────────────────────────────── */

const WhatsAppSVG = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="22"
    height="22"
    viewBox="0 0 24 24"
    fill="white"
    aria-hidden="true"
    focusable="false"
  >
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.890-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
  </svg>
);

/* ── Component ─────────────────────────────────────────────────────────────── */

export default function WhatsAppButton() {
  const { pathname } = useLocation();

  // Gate: only mount animation/timer logic on public routes
  const isPublic = isPublicRoute(pathname);

  // `shown` drives the fade-in (deferred by APPEAR_DELAY_MS)
  const [shown, setShown] = useState(false);
  // `showTooltip` shows the "Need assistance?" tip for a few seconds
  const [showTooltip, setShowTooltip] = useState(false);

  /* Reset + schedule appear on every public pathname change */
  useEffect(() => {
    if (!isPublic) {
      setShown(false);
      setShowTooltip(false);
      return;
    }

    setShown(false);
    const appear = setTimeout(() => {
      setShown(true);
      setShowTooltip(true);
    }, APPEAR_DELAY_MS);

    return () => clearTimeout(appear);
  }, [isPublic, pathname]);

  /* Auto-hide tooltip after it appears */
  useEffect(() => {
    if (!showTooltip) return;
    const hide = setTimeout(() => setShowTooltip(false), TOOLTIP_HIDE_DELAY_MS);
    return () => clearTimeout(hide);
  }, [showTooltip]);

  const handleClick = useCallback(() => {
    const clean = WHATSAPP_NUMBER.replace(/\D/g, '');
    if (!clean) return;
    const encoded = encodeURIComponent(WHATSAPP_MESSAGE || ' ');
    window.open(
      `https://wa.me/${clean}?text=${encoded}`,
      '_blank',
      'noopener,noreferrer'
    );
  }, []);

  /* Guard: do not render on protected routes or if phone number is missing */
  if (!isPublic || !WHATSAPP_NUMBER) return null;

  /* Keep component in DOM but invisible until delay fires (enables animation) */
  if (!shown) return null;

  return (
    <div className="wa-wrapper" role="complementary" aria-label="WhatsApp support">
      {showTooltip && (
        <div className="wa-tooltip" role="tooltip">
          Need assistance?
        </div>
      )}
      <button
        className="wa-button"
        type="button"
        onClick={handleClick}
        aria-label="Chat with us on WhatsApp"
      >
        <span className="wa-icon">
          <WhatsAppSVG />
        </span>
        <span className="wa-text">Need Help? Chat with us</span>
      </button>
    </div>
  );
}
